'use client';

import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type ButtonHTMLAttributes,
} from 'react';

import {
  SelectPopover,
  SelectChoices,
} from '@eliadmin/core/src/components/nt/ui/searchable-select';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Form controls of the Styleguide (164:12496 Input, 164:12505 Checkbox, 184:14824 Toggle). They
 * forward refs, so `register()` of react-hook-form works on them directly. Focus = green border
 * + the `shadow-focus` ring (Figma has no focus state).
 */
const BOX =
  'flex h-12 w-full items-center gap-2 rounded-xl border bg-white px-[14px] transition-colors focus-within:shadow-focus';
/** Border by state: an invalid field stays red while focused. */
const border = (invalid?: boolean) =>
  invalid ? 'border-[#D92D20]' : 'border-[#E2E5DF] focus-within:border-[#127A26]';

type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean;
  /** Right slot inside the box – „Vyhľadať“, „Zobraziť“ (Figma Action, Body/M Bold #0E6A20). */
  action?: ReactNode;
  boxClassName?: string;
};

/** 48 px box, radius 12, #E2E5DF border (#D92D20 when invalid), Body/L value, #8F978E placeholder. */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { invalid, action, className, boxClassName, disabled, ...props },
  ref,
) {
  return (
    <div className={cn(BOX, border(invalid), disabled && 'bg-[#F5F6F3]', boxClassName)}>
      <input
        ref={ref}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(
          'nt-body-l min-w-0 flex-1 bg-transparent text-[#0D110D] outline-none placeholder:text-[#8F978E] disabled:text-[#8F978E]',
          className,
        )}
        {...props}
      />
      {action}
    </div>
  );
});

/** Action inside a `TextInput` box (Body/M Bold #0E6A20). */
export function InputAction({
  children,
  onClick,
  disabled,
  label,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="nt-body-m-bold shrink-0 rounded text-[#0E6A20] hover:text-[#0B5719] focus-visible:shadow-focus focus-visible:outline-none disabled:text-[#8F978E]"
    >
      {children}
    </button>
  );
}

type SelectInputProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'value' | 'onChange' | 'children'
> & {
  invalid?: boolean;
  value: string | number;
  onChange: (value: string) => void;
  options: { value: string; label: string; disabled?: boolean }[];
  label: string;
  placeholder?: string;
  autoComplete?: string;
};

/** Controlled searchable select; RHF controllers own values, resets and validation. */
export const SelectInput = forwardRef<HTMLButtonElement, SelectInputProps>(function SelectInput(
  {
    invalid,
    className,
    value,
    onChange,
    options,
    label,
    placeholder = 'Vyberte položku',
    name,
    autoComplete,
    ...props
  },
  ref,
) {
  return (
    <>
      {name ? (
        <input
          type="hidden"
          name={name}
          value={value}
          disabled={props.disabled}
          autoComplete={autoComplete}
        />
      ) : null}
      <SelectPopover
        label={label}
        trigger={
          <button
            {...props}
            ref={ref}
            type="button"
            aria-invalid={invalid || undefined}
            className={cn(
              BOX,
              border(invalid),
              'nt-body-l justify-between text-left text-[#0D110D] disabled:cursor-not-allowed disabled:bg-[#F5F6F3] disabled:text-[#8F978E]',
              className,
            )}
          >
            <span className="min-w-0 truncate">
              {options.find(option => option.value === String(value))?.label || placeholder}
            </span>
            <Icon name="caret-down" size={16} className="shrink-0" />
          </button>
        }
      >
        {close => (
          <SelectChoices
            options={options}
            selected={String(value)}
            label={`Hľadať: ${label}`}
            placeholder="Hľadať…"
            onChoose={next => {
              onChange(next);
              close();
            }}
          />
        )}
      </SelectPopover>
    </>
  );
});

type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: ReactNode;
  invalid?: boolean;
};

/** 20 px box (radius 6, 1.5 px #E2E5DF; checked #127A26 + white check 14), Body/L label, gap 10. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, invalid, className, id, ...props },
  ref,
) {
  return (
    <label htmlFor={id} className={cn('group flex cursor-pointer items-start gap-2.5', className)}>
      <span className="relative mt-px flex size-5 shrink-0">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          aria-invalid={invalid || undefined}
          className={cn(
            'peer size-5 cursor-pointer appearance-none rounded-[6px] border-[1.5px] bg-white transition-colors checked:border-[#127A26] checked:bg-[#127A26] focus-visible:shadow-focus focus-visible:outline-none',
            invalid ? 'border-[#D92D20]' : 'border-[#E2E5DF]',
          )}
          {...props}
        />
        <Icon
          name="check"
          size={14}
          className="pointer-events-none absolute left-[3px] top-[3px] text-white opacity-0 peer-checked:opacity-100"
        />
      </span>
      <span className="nt-body-l text-[#0D110D]">{label}</span>
    </label>
  );
});

type ToggleProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'role'>;

/** 44 × 24 switch: on #127A26, off / disabled #E2E5DF, white 20 px knob 2 px from the edge. */
export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
  { className, ...props },
  ref,
) {
  return (
    <span className={cn('relative inline-flex h-6 w-11 shrink-0', className)}>
      <input
        ref={ref}
        type="checkbox"
        role="switch"
        className="peer absolute inset-0 cursor-pointer appearance-none rounded-full bg-[#E2E5DF] transition-colors checked:bg-[#127A26] focus-visible:shadow-focus focus-visible:outline-none disabled:cursor-not-allowed"
        {...props}
      />
      <span className="pointer-events-none absolute left-0.5 top-0.5 size-5 rounded-full bg-white shadow-[0_1px_2px_rgba(13,17,13,0.2)] transition-transform peer-checked:translate-x-5" />
    </span>
  );
});
